import { useSetAtom } from "jotai";
import { useRef } from "react";

import {
  getElementOverlayGeometry,
  getTransformOriginFractions,
  projectScreenDeltaToLocal,
  useDragDropManager,
  useDragDropMonitor,
} from "@skilitics/drag-drop";

import { useEventPublish } from "../../events/index.ts";
import { DND_CANVAS_EDITOR_ID, isTransformOriginId, parseTransformOriginId } from "../dnd/index.ts";
import { getNodeElement } from "../element-state/utils.ts";
import { propertiesEventTypes } from "../properties/propertiesEventTypes.ts";
import { canvasTransformOriginDragAtom } from "./canvasAtoms.ts";

export const useTransformOriginDragMonitor = () => {
  const publishEvent = useEventPublish();
  const setDragState = useSetAtom(canvasTransformOriginDragAtom);
  const manager = useDragDropManager();
  const startFractionsRef = useRef<{ ox: number; oy: number } | null>(null);
  // Captures the last fractions computed in onDragMove so onDragEnd can publish
  // the exact same value, avoiding recomputation from manager.dragOperation.transform
  // which can diverge for rotated elements.
  const lastFractionsRef = useRef<{ ox: number; oy: number } | null>(null);
  // Geometry is invariant during an origin drag — the element isn't moved or resized,
  // and the new origin is only written on drag end — so capture it once at start
  // instead of forcing a layout/reflow on every pointer-move frame.
  const startGeometryRef = useRef<{
    rotation: number;
    scaleX: number;
    scaleY: number;
    intrinsicWidth: number;
    intrinsicHeight: number;
  } | null>(null);
  // Skip frames where the snap-adjusted transform hasn't moved.
  const lastTransformRef = useRef<{ x: number; y: number } | null>(null);

  useDragDropMonitor({
    onDragStart(event) {
      const source = event.operation.source;
      if (!source || !isTransformOriginId(source.id)) return;

      const nodeId = parseTransformOriginId(source.id);
      const nodeElement = getNodeElement(nodeId);
      if (!nodeElement) return;

      const nodeGeometry = getElementOverlayGeometry(nodeElement, document.getElementById(DND_CANVAS_EDITOR_ID));
      startGeometryRef.current = {
        rotation: nodeGeometry.rotation,
        scaleX: nodeGeometry.scaleX,
        scaleY: nodeGeometry.scaleY,
        intrinsicWidth: nodeGeometry.intrinsicRect.width,
        intrinsicHeight: nodeGeometry.intrinsicRect.height,
      };
      startFractionsRef.current = getTransformOriginFractions(
        nodeElement,
        nodeGeometry.intrinsicRect.width,
        nodeGeometry.intrinsicRect.height,
      );
      lastFractionsRef.current = null;
      lastTransformRef.current = null;
    },

    onDragMove(event) {
      const source = event.operation.source;
      if (!source || !isTransformOriginId(source.id)) return;

      const startGeometry = startGeometryRef.current;
      const startFractions = startFractionsRef.current;
      if (!startGeometry || !startFractions) return;

      const transform = manager?.dragOperation.transform;
      if (!transform) return;

      const prev = lastTransformRef.current;
      if (prev && prev.x === transform.x && prev.y === transform.y) return;
      lastTransformRef.current = { x: transform.x, y: transform.y };

      const { rotation, scaleX, scaleY, intrinsicWidth, intrinsicHeight } = startGeometry;
      if (intrinsicWidth === 0 || intrinsicHeight === 0) return;

      const localDelta = projectScreenDeltaToLocal(transform, rotation, scaleX, scaleY);
      const { ox, oy } = startFractions;
      const newFractions = {
        ox: ox + localDelta.x / intrinsicWidth,
        oy: oy + localDelta.y / intrinsicHeight,
      };

      lastFractionsRef.current = newFractions;
      setDragState({ nodeId: parseTransformOriginId(source.id), fractions: newFractions });
    },

    onDragEnd(event) {
      const source = event.operation.source;
      const capturedFractions = lastFractionsRef.current;
      const capturedStartFractions = startFractionsRef.current;
      startFractionsRef.current = null;
      lastFractionsRef.current = null;
      startGeometryRef.current = null;
      lastTransformRef.current = null;
      setDragState(undefined);

      if (!source || !isTransformOriginId(source.id) || event.canceled || !capturedFractions || !capturedStartFractions)
        return;

      const nodeId = parseTransformOriginId(source.id);

      publishEvent(propertiesEventTypes.TRANSFORM_ORIGIN_CHANGE, {
        nodeId,
        fractions: capturedFractions,
        startFractions: capturedStartFractions,
      });
    },
  });
};
